import { useEstimateStore } from '@talon/web/src/context';
import { DateString, FormErrors } from '@talon/web/src/types';
import dayjs from 'dayjs';
import { Box, Button, DateInput, Keyboard, Layer, Text } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { TalonInput } from '..';

export interface ICreateSessionEstimateModalProps {
  addSessionEstimate: (title: string, date: DateString) => Promise<void>;
  setShowModal: (isVisible: boolean) => void;
}

export const CreateSessionEstimateModal = observer(
  ({ addSessionEstimate, setShowModal }: ICreateSessionEstimateModalProps) => {
    /** Context **/
    const estimateStore = useEstimateStore();

    /** State **/
    // .toISOString() (not .format(), which converts to local time) keeps this a UTC-safe
    // date-only string - the "date" column round-trips through the API as UTC midnight, so
    // formatting via local time can roll the day back or forward depending on the viewer's
    // timezone offset.
    const [date, setDate] = useState(
      estimateStore.lastSessionEstimateDate
        ? dayjs(estimateStore.lastSessionEstimateDate).add(1, 'day').toISOString().slice(0, 10)
        : ''
    );
    const [title, setTitle] = useState(
      `Session #${(estimateStore.currentEstimate?.session_estimates.length ?? 0) + 1}`
    );

    const [formErrors, setFormErrors] = useState<FormErrors>({});

    /** Methods **/
    const createSessionEstimate = () => {
      if (estimateStore.getSessionEstimateDates().find((d) => dayjs(d).format('YYYY-MM-DD') === date)) {
        setFormErrors({ ...formErrors, date: 'A Session Estimate already exists for this date.' });
        return;
      }

      addSessionEstimate(title, date);
      setShowModal(false);
    };

    /** Render **/
    return (
      <Layer
        onEsc={() => setShowModal(false)}
        onClickOutside={() => setShowModal(false)}
        position="center"
        modal={true}
        margin="large"
      >
        <Box
          direction="column"
          align="start"
          pad="small"
          justify="around"
          width="medium"
          height="medium"
          background="light-1"
          round="small"
        >
          <Box direction="row" pad="small" gap="small" align="center" fill="horizontal">
            <Box width="20%">
              <Text>Title:</Text>
            </Box>
            <Box fill="horizontal">
              <TalonInput
                val={title || ''}
                onChange={(val) => setTitle(val)}
                onSubmit={() => createSessionEstimate()}
              />
            </Box>
          </Box>
          <Box>
            <Box direction="row" pad="small" gap="small" align="center" fill="horizontal">
              <Box width="20%">
                <Text>Date:</Text>
              </Box>
              <Box fill="horizontal" gap="xsmall">
                <Keyboard onEnter={createSessionEstimate}>
                  <DateInput
                    format="mm/dd/yyyy"
                    value={date}
                    calendarProps={{ disabled: estimateStore.getSessionEstimateDates() }}
                    onChange={({ value }) => setDate(typeof value === 'object' ? value[0] : value)}
                  />
                </Keyboard>
                {formErrors.date && (
                  <Box fill="horizontal" align="end">
                    <Text size="xsmall" color="red">
                      {formErrors.date}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Button
            pad="small"
            margin="small"
            hoverIndicator
            onClick={() => createSessionEstimate()}
            label="Add Session Estimate"
          />
        </Box>
      </Layer>
    );
  }
);
